import { computed, ref } from "vue";
import type { CounterpartyModel } from "@/components/common/CounterpartyPicker.vue";
import type { PickedCompany } from "@/types";
import type { RegistrationAuthority } from "@/utils/registryAccounts";

interface LinkedCompany {
  id: number;
  name: string;
}

/**
 * Owns a counterparty's fields, including the optional link to a
 * global_company. Shared by the sales-transaction form, the forward deal
 * metadata modal and the Soldera Inventory offer terms. The linked id and name
 * live in a single ref so they cannot drift apart — the FK invariant
 * ("id is set iff the snapshot name matches the linked company") is a
 * property of the type rather than something defended at every touch point.
 */
export function useCounterpartyForm() {
  const counterpartyName = ref("");
  const counterpartyCountry = ref("");
  const counterpartyEecsAccountNumber = ref("");
  const counterpartyEmails = ref("");
  const registrationAuthority = ref<RegistrationAuthority | null>(null);
  const selectedGlobalCompany = ref<LinkedCompany | null>(null);

  /**
   * Ownership split on company pick: CounterpartyPicker writes companyName and
   * accountNumber through `counterpartyModel`; this handler owns only what the
   * picker cannot know — the global_company link, the billing country, and the
   * emails prefill. Keeping each field single-writer makes the final state
   * independent of whether the model write or this emit lands first.
   */
  const handleCompanySelected = (company: PickedCompany) => {
    selectedGlobalCompany.value = { id: company.id, name: company.name };
    if (company.country) {
      counterpartyCountry.value = company.country;
    }
    // Prefill emails only when blank so a manually-typed buyer address is never clobbered.
    if (company.emails && !counterpartyEmails.value) {
      counterpartyEmails.value = company.emails;
    }
  };

  const handleCounterpartyNameUpdate = (newName: string) => {
    counterpartyName.value = newName;
    if (
      selectedGlobalCompany.value !== null &&
      newName !== selectedGlobalCompany.value.name
    ) {
      selectedGlobalCompany.value = null;
    }
  };

  /**
   * Seeds the global_company link from a loaded transaction. The save-time
   * invariant guarantees `counterparty_name` matches the linked global_company
   * name whenever `global_company_id` is set, so the snapshot name is a
   * faithful baseline for divergence detection.
   */
  const hydrateLinkedCompany = (transaction: {
    counterparty_name: string | null;
    global_company_id: number | null;
  }) => {
    selectedGlobalCompany.value =
      transaction.global_company_id !== null &&
      transaction.counterparty_name !== null
        ? {
            id: transaction.global_company_id,
            name: transaction.counterparty_name,
          }
        : null;
  };

  /**
   * v-model bridge for CounterpartyPicker over the flat refs. Name changes are
   * routed through the divergence handler so the global_company link invariant
   * is enforced at this single write point rather than in the consuming view.
   */
  const counterpartyModel = computed<CounterpartyModel>({
    get: () => ({
      companyName: counterpartyName.value,
      accountNumber: counterpartyEecsAccountNumber.value,
    }),
    set: (value: CounterpartyModel) => {
      if (value.companyName !== counterpartyName.value) {
        handleCounterpartyNameUpdate(value.companyName);
      }
      counterpartyEecsAccountNumber.value = value.accountNumber;
    },
  });

  return {
    counterpartyName,
    counterpartyCountry,
    counterpartyEecsAccountNumber,
    counterpartyEmails,
    registrationAuthority,
    counterpartyModel,
    selectedGlobalCompany,
    handleCompanySelected,
    handleCounterpartyNameUpdate,
    hydrateLinkedCompany,
  };
}
